/**
 * WizardHelp
 *
 * Right panel providing context-sensitive help and information.
 * Shows "Why we ask" explanations and relevant tips.
 * Collapsible with localStorage persistence.
 */

import { useState, useEffect } from 'react';
import { type WizardStep, type WizardBasicsData } from '@/types/wizard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HelpCircle, Lightbulb, MapPin, AlertTriangle, ChevronLeft, ChevronRight, ClipboardList, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useJurisdictionDetail } from '@/hooks/useJurisdictions';

const STORAGE_KEY = 'wizard-help-collapsed';
const STORAGE_KEY_USER_TOGGLED = 'wizard-help-user-toggled';

interface WizardHelpProps {
  currentStep: WizardStep;
  basicsData?: WizardBasicsData;
  /** Number of blockers/issues - auto-expands help if > 0 */
  blockerCount?: number;
}

// Help content for each step
const stepHelp: Record<WizardStep, { title: string; content: string; tips?: string[] }> = {
  entry: {
    title: 'Getting Started',
    content: 'Choose whether you\'re pulling a permit for a single property or managing a multi-unit development project.',
    tips: [
      'Single Property: Kitchen remodels, panel upgrades, re-roofs, etc.',
      'Development Project: Subdivisions, townhomes, apartment complexes',
      'Development projects help you manage permits across multiple units',
      'You can create individual unit permits from a project dashboard',
    ],
  },
  project_type: {
    title: 'Development Type',
    content: 'Different development types have different permit patterns and unit structures. Select the type that best matches your project.',
    tips: [
      'Subdivision: Individual lots with separate addresses',
      'Townhome: Attached homes typically sharing walls',
      'Apartment: Multi-family buildings with rental units',
      'Mixed-Use: Commercial + residential in one development',
      'Commercial Complex: Multiple commercial buildings/spaces',
    ],
  },
  project_basics: {
    title: 'Project Information',
    content: 'Name your project and provide the main address for the development. This address is used for jurisdiction lookup.',
    tips: [
      'The project name helps you identify this development',
      'Use the main site address or entrance for jurisdiction lookup',
      'Individual units can have different addresses later',
      'The jurisdiction determines permit requirements and fees',
    ],
  },
  units: {
    title: 'Unit Setup',
    content: 'Define how many units are in your development and how they should be labeled. You can modify units later from the project page.',
    tips: [
      'Labels help identify each unit (Lot 1, Unit A, Building 3)',
      'Choose a label style that matches your project documents',
      'Units can be lots, townhomes, apartments, or buildings',
      'You can add or remove units later as plans change',
    ],
  },
  basics: {
    title: 'Why We Ask',
    content: 'Your project type, contractor role, and location determine which permits are required and who is responsible for pulling them.',
    tips: [
      'Residential and commercial projects have different requirements',
      'Your contractor role determines permit pulling responsibility',
      'GCs typically pull Building permits; subs may pull their own trade permits',
      'Owner-Builders have specific requirements and limitations',
      'The exact address helps us identify your local AHJ (Authority Having Jurisdiction)',
    ],
  },
  trades: {
    title: 'Trade Selection',
    content: 'Different types of work require permits from different departments. Selecting all applicable trades ensures you get complete permit guidance.',
    tips: [
      'Multi-trade projects may need separate permits for each trade',
      'Some jurisdictions offer combined permits for common combinations',
      'General contractors often pull permits for multiple trades',
    ],
  },
  scopes: {
    title: 'Scope Details',
    content: 'The specific type of work within each trade affects documentation requirements and inspection checkpoints.',
    tips: [
      'Panel upgrades have different requirements than new circuit installations',
      'Scope details help us recommend the right documents and inspections',
      'Be specific to avoid surprises during plan review',
    ],
  },
  permits: {
    title: 'Permit Recommendations',
    content: 'Based on your project scope, we recommend the permits you\'ll need. You can accept our recommendations or override them.',
    tips: [
      'HIGH confidence means we\'re very sure this permit is required',
      'MEDIUM confidence items may depend on specific project details',
      'You can always override recommendations, but be sure to verify with your AHJ',
    ],
  },
  details: {
    title: 'Project Details',
    content: 'These additional details help determine conditional requirements like Notice of Commencement (NOC) and owner-builder provisions.',
    tips: [
      'NOC thresholds vary by jurisdiction - we\'ll flag it if it applies',
      'Owner-builder permits have specific requirements and limitations',
      'Commercial projects often need additional engineering reviews',
    ],
  },
  review: {
    title: 'Final Review',
    content: 'Review all your information before creating your permit applications. You can go back to any step to make changes.',
    tips: [
      'Double-check your address and jurisdiction',
      'Verify all trades and scopes are correct',
      'You can always edit jobs after creation',
    ],
  },
};

export function WizardHelp({ currentStep, basicsData, blockerCount = 0 }: WizardHelpProps) {
  const help = stepHelp[currentStep];
  const { data: jurisdictionDetail } = useJurisdictionDetail(basicsData?.jurisdictionId ?? null);
  const propertyType = basicsData?.propertyType;
  const permitProcess = propertyType === 'commercial'
    ? jurisdictionDetail?.permitting_process?.commercial
    : propertyType === 'residential'
      ? jurisdictionDetail?.permitting_process?.residential
      : null;
  const permitRequirements = propertyType === 'commercial'
    ? jurisdictionDetail?.commercial_requirements
    : null;
  const portal = jurisdictionDetail?.online_portal;
  const processLabel = propertyType === 'commercial'
    ? 'Commercial'
    : propertyType === 'residential'
      ? 'Residential'
      : 'Permit';
  const stepPreview = permitProcess?.steps?.slice(0, 6) ?? [];
  const hasMoreSteps = (permitProcess?.steps?.length ?? 0) > stepPreview.length;
  const showPermitGuide = !!propertyType && stepPreview.length > 0;

  // Track if user has manually toggled (for progressive disclosure)
  const [userHasToggled, setUserHasToggled] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_USER_TOGGLED) === 'true';
    }
    return false;
  });

  // Progressive disclosure:
  // - Default to COLLAPSED (less overwhelming)
  // - Auto-expand if blockers exist AND user hasn't manually collapsed
  // - First step ('basics') starts expanded to onboard users
  const hasBlockers = blockerCount > 0;
  const isFirstStep = currentStep === 'entry';

  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved !== null) {
        return saved === 'true';
      }
    }
    // Default to expanded only on first step or if blockers exist
    return !(isFirstStep || hasBlockers);
  });

  // Auto-expand when blockers appear (only if user hasn't explicitly collapsed)
  useEffect(() => {
    if (hasBlockers && !userHasToggled) {
      setIsCollapsed(false);
    }
  }, [hasBlockers, userHasToggled]);

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
    setUserHasToggled(true);
    localStorage.setItem(STORAGE_KEY_USER_TOGGLED, 'true');
  };

  return (
    <aside
      className={cn(
        "hidden lg:flex flex-col border-l bg-muted/30 transition-all duration-200",
        isCollapsed ? "w-12" : "w-80"
      )}
    >
      {/* Toggle Button */}
      <div className={cn(
        "flex items-center border-b p-2",
        isCollapsed ? "justify-center" : "justify-between"
      )}>
        {!isCollapsed && (
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2">
            Help & Tips
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleCollapsed}
          className="h-8 w-8 p-0"
          title={isCollapsed ? "Show help panel" : "Hide help panel"}
        >
          {isCollapsed ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Collapsed State - Just show icon */}
      {isCollapsed ? (
        <div className="flex-1 flex flex-col items-center pt-4 gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className="h-8 w-8 p-0"
            title="Show help"
          >
            <HelpCircle className="h-5 w-5 text-primary" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className="h-8 w-8 p-0"
            title="Show tips"
          >
            <Lightbulb className="h-5 w-5 text-muted-foreground" />
          </Button>
          {basicsData?.jurisdictionName && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapsed}
              className="h-8 w-8 p-0"
              title="Show jurisdiction info"
            >
              <MapPin className="h-5 w-5 text-muted-foreground" />
            </Button>
          )}
        </div>
      ) : (
        /* Expanded State - Full content */
        <div className="flex-1 overflow-y-auto p-4">
          {/* Context Card */}
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-primary" />
                <CardTitle className="text-base">{help.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{help.content}</p>
            </CardContent>
          </Card>

          {/* Tips */}
          {help.tips && help.tips.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-base">Tips</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {help.tips.map((tip, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex gap-2">
                      <span className="text-muted-foreground">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Jurisdiction Info (if available) */}
          {basicsData?.jurisdictionName && (
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-base">Jurisdiction</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{basicsData.jurisdictionName}</p>
                {basicsData.address?.county && (
                  <p className="text-sm text-muted-foreground">
                    {basicsData.address.county} County, {basicsData.address.state}
                  </p>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  {basicsData.isHvhz && (
                    <Badge variant="secondary" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      HVHZ
                    </Badge>
                  )}
                  {basicsData.isFloodZone && (
                    <Badge variant="secondary" className="text-xs">
                      Flood Zone
                    </Badge>
                  )}
                  {basicsData.isCoastalZone && (
                    <Badge variant="secondary" className="text-xs">
                      Coastal Zone
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {showPermitGuide && (
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-base">{processLabel} Permit Steps</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {permitProcess?.overview && (
                  <p className="text-sm text-muted-foreground">
                    {permitProcess.overview}
                  </p>
                )}
                <ol className="space-y-2">
                  {stepPreview.map((step) => (
                    <li key={step.order} className="flex gap-2">
                      <Badge variant="secondary" className="h-5 px-2 text-xs">
                        Step {step.order}
                      </Badge>
                      <div>
                        <p className="text-sm font-medium text-foreground">{step.name}</p>
                        <p className="text-xs text-muted-foreground">{step.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                {hasMoreSteps && (
                  <p className="text-xs text-muted-foreground">
                    + {permitProcess?.steps.length ? permitProcess.steps.length - stepPreview.length : 0} more steps
                  </p>
                )}
                {portal?.url && (
                  <div className="rounded-lg bg-muted/50 p-3 space-y-2">
                    <p className="text-xs font-medium text-foreground">
                      Submit in {portal.name} (external portal)
                    </p>
                    {permitRequirements?.digital_sealing && (
                      <p className="text-xs text-muted-foreground">
                        {permitRequirements.digital_sealing}
                      </p>
                    )}
                    <a
                      href={portal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      Open portal <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </aside>
  );
}
